/**
 * Feature Flags Context - Contexto React para manejar feature flags
 */

import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type { FeatureFlags } from '../services/feature-flags.service';
import { featureFlagsService } from '../services/feature-flags.service';

interface FeatureFlagsContextType {
  flags: FeatureFlags;
  isEnabled: (flag: keyof FeatureFlags) => boolean;
  setFlag: (flag: keyof FeatureFlags, enabled: boolean) => void;
  toggleFlag: (flag: keyof FeatureFlags) => void;
  resetToDefaults: () => void;
  debugConfig: {
    enabled: boolean;
    showMigration: boolean;
    showAuthDiagnostics: boolean;
    showFirebaseHelper: boolean;
    showSmartUploader: boolean;
    showDemoCreator: boolean;
  };
}

const FeatureFlagsContext = createContext<FeatureFlagsContextType | null>(null);

interface FeatureFlagsProviderProps {
  children: ReactNode;
}

export function FeatureFlagsProvider({ children }: FeatureFlagsProviderProps) {
  const [flags, setFlags] = useState<FeatureFlags>(featureFlagsService.getAllFlags());

  useEffect(() => {
    // Suscribirse a cambios en el servicio
    const unsubscribe = featureFlagsService.subscribe((newFlags) => {
      setFlags(newFlags);
    });

    return unsubscribe;
  }, []);

  const contextValue: FeatureFlagsContextType = {
    flags,
    isEnabled: (flag: keyof FeatureFlags) => featureFlagsService.isEnabled(flag),
    setFlag: (flag: keyof FeatureFlags, enabled: boolean) => featureFlagsService.setFlag(flag, enabled),
    toggleFlag: (flag: keyof FeatureFlags) => featureFlagsService.toggleFlag(flag),
    resetToDefaults: () => featureFlagsService.resetToDefaults(),
    debugConfig: featureFlagsService.getDebugConfig(),
  };

  return (
    <FeatureFlagsContext.Provider value={contextValue}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

/**
 * Hook para usar feature flags en componentes
 */
export function useFeatureFlags(): FeatureFlagsContextType {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagsProvider');
  }
  return context;
}

/**
 * Hook simplificado para verificar si una flag está habilitada
 */
export function useFeatureFlag(flag: keyof FeatureFlags): boolean {
  const { isEnabled } = useFeatureFlags();
  return isEnabled(flag);
}